import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Phone, Check, Clock, LogOut, RefreshCw, ChevronLeft, ChevronRight, List, Columns, Bell, AlertTriangle, Star } from "lucide-react";

// Mobile-first one-page booking monitor for the owner / family / mobile
// receptionist. Designed strictly for phone-screen widths — no sidebar, no
// turn board, no settings. Permissions match receptionist (read/confirm/late).
// Two view modes:
//  • "list"     — flat timeline of bookings (default; quick triage)
//  • "calendar" — horizontal-scroll grid with every staff column visible,
//                 so the owner can see the whole shop at a glance on a phone
export default function MobileBookings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Format YYYY-MM-DD from a Date using LOCAL date parts (NOT toISOString,
  // which returns UTC and rolls the day over when the browser's local
  // wall-clock crosses midnight relative to UTC).
  const localYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const [date, setDate] = useState(() => localYMD(new Date()));
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all|booked|confirmed|checked_in|done
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("mb_view") || "list");
  useEffect(() => { localStorage.setItem("mb_view", view); }, [view]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s, sv] = await Promise.all([
        api.get(`/admin/bookings?date_from=${date}&date_to=${date}`),
        api.get("/admin/staff"),
        api.get("/public/services"),
      ]);
      setBookings(b.data || []);
      setStaff(s.data || []);
      setServices(sv.data || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  // Auto-refresh every 45s so monitor screen stays live without taps
  useEffect(() => {
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [date]);

  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const svcById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);

  const filtered = useMemo(() => {
    let list = bookings.filter((b) => !b.deleted && !b.is_block_time);
    if (filter !== "all") list = list.filter((b) => b.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((b) =>
        (b.customer_name || "").toLowerCase().includes(q) ||
        (b.customer_phone || "").includes(q));
    }
    return list.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  }, [bookings, filter, search]);

  const shift = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(localYMD(d));
  };
  const isToday = date === localYMD(new Date());

  const confirm = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/confirm`); toast.success("Confirmed + SMS sent"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const checkIn = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/check-in`); toast.success("Checked in"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const markLate = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/late`); toast.success("Marked late + SMS"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const counts = useMemo(() => ({
    all: bookings.filter((b) => !b.deleted && !b.is_block_time).length,
    booked: bookings.filter((b) => b.status === "booked").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    checked_in: bookings.filter((b) => b.status === "checked_in").length,
  }), [bookings]);

  return (
    <div className="min-h-screen bg-zen-surface text-zen-ink" data-testid="mobile-bookings">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-white border-b border-zen-border shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="font-serif text-lg leading-none">Bookings</div>
          <div className="flex items-center gap-1">
            {/* View toggle: list <-> calendar (all-staff column grid) */}
            <div className="flex rounded-full bg-zen-surface p-0.5" data-testid="mb-view-toggle">
              <button onClick={() => setView("list")}
                className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${view === "list" ? "bg-white shadow font-semibold" : "text-zen-muted"}`}
                aria-label="List view" data-testid="mb-view-list">
                <List className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setView("calendar")}
                className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${view === "calendar" ? "bg-white shadow font-semibold" : "text-zen-muted"}`}
                aria-label="Calendar view" data-testid="mb-view-cal">
                <Columns className="w-3.5 h-3.5" />
              </button>
            </div>
            <button onClick={load} className="p-2 rounded-full hover:bg-zen-surface" data-testid="mb-refresh"
              aria-label="Refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={() => { logout(); navigate("/login"); }} className="p-2 rounded-full hover:bg-zen-surface" data-testid="mb-logout"
              aria-label="Logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
        {/* Date stepper */}
        <div className="flex items-center justify-between px-4 pb-2">
          <button onClick={() => shift(-1)} className="p-2 rounded-full hover:bg-zen-surface" data-testid="mb-prev-day"><ChevronLeft className="w-5 h-5" /></button>
          <div className="text-center">
            <div className="font-semibold text-sm" data-testid="mb-date">{new Date(date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
            {!isToday && (
              <button onClick={() => setDate(localYMD(new Date()))}
                className="text-[11px] text-zen-primary underline" data-testid="mb-today">Jump to today</button>
            )}
          </div>
          <button onClick={() => shift(1)} className="p-2 rounded-full hover:bg-zen-surface" data-testid="mb-next-day"><ChevronRight className="w-5 h-5" /></button>
        </div>
        {/* Filter pills + search — list view only. In calendar view the
            whole grid speaks for itself, so we hide the controls to maximise
            screen real estate. */}
        {view === "list" && (
          <>
            <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto" data-testid="mb-filters">
              {[
                { k: "all", label: `All (${counts.all})` },
                { k: "booked", label: `New (${counts.booked})` },
                { k: "confirmed", label: `Confirmed (${counts.confirmed})` },
                { k: "checked_in", label: `Checked-in (${counts.checked_in})` },
              ].map((f) => (
                <button key={f.k} onClick={() => setFilter(f.k)}
                  className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap border ${filter === f.k
                    ? "bg-zen-primary text-white border-zen-primary"
                    : "bg-white border-zen-border text-zen-ink"}`}
                  data-testid={`mb-filter-${f.k}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="px-3 pb-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or phone…"
                className="w-full h-9 px-3 rounded-full bg-zen-surface text-sm border-0 focus:outline-none focus:ring-2 focus:ring-zen-primary/40"
                data-testid="mb-search"
              />
            </div>
          </>
        )}
      </div>

      {/* Calendar view — horizontal scroll grid; one narrow column per staff,
          fixed time-rail on the left. Read-only (tap a card to call). */}
      {view === "calendar" && (
        <CalendarGrid
          bookings={bookings}
          staff={staff}
          svcById={svcById}
        />
      )}

      {/* List view */}
      {view === "list" && (
      <div className="px-3 py-3 space-y-2 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center text-zen-muted text-sm py-12" data-testid="mb-empty">
            {loading ? "Loading…" : "No bookings match this filter."}
          </div>
        ) : filtered.map((b) => {
          const tech = staffById[b.staff_id]?.full_name || "—";
          const svcNames = (b.service_ids || []).map((id) => svcById[id]?.name).filter(Boolean).join(", ") || "—";
          const isConfirmed = b.status === "confirmed";
          const isCheckedIn = b.status === "checked_in";
          const isBooked = b.status === "booked";
          return (
            <div key={b.id} className="bg-white rounded-2xl border border-zen-border p-3 shadow-sm" data-testid={`mb-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-semibold text-zen-ink truncate">{b.customer_name}</div>
                    {(b.customer_notes || "").trim() && (
                      <span className="bg-amber-300 text-amber-900 px-1 rounded-full leading-none py-0.5 shrink-0"
                        title={b.customer_notes}
                        data-testid={`mb-note-bell-${b.id}`}>
                        <Bell className="w-3 h-3" strokeWidth={2.5} fill="currentColor" />
                      </span>
                    )}
                    {(b.customer_blacklisted || (b.customer_staff_note || "").trim()) && (
                      <span className="bg-red-700 text-white px-1 rounded-full leading-none py-0.5 shrink-0 border border-red-900 shadow-sm"
                        title={b.customer_blacklisted
                          ? `⚠ BLACKLISTED — ${(b.customer_staff_note || "").trim() || "Do not accept"}`
                          : `⚠ Sổ đen: ${(b.customer_staff_note || "").trim()}`}
                        data-testid={`mb-sodden-${b.id}`}>
                        <AlertTriangle className="w-3 h-3" strokeWidth={3} fill="currentColor" />
                      </span>
                    )}
                    {b.customer_review_requested && (
                      <span className="bg-yellow-400 text-yellow-900 px-1 rounded-full leading-none py-0.5 shrink-0 shadow-sm"
                        title="Google review already requested — don't ask again"
                        data-testid={`mb-review-asked-${b.id}`}>
                        <Star className="w-3 h-3" strokeWidth={2.5} fill="currentColor" />
                      </span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                      b.requested_any ? "bg-emerald-700 text-white" : "bg-red-700 text-white"}`}
                      data-testid={`mb-intent-${b.id}`}>
                      {b.requested_any ? "Assign" : "Request"}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                      isCheckedIn ? "bg-emerald-100 text-emerald-800"
                      : isConfirmed ? "bg-blue-100 text-blue-800"
                      : "bg-zen-cal-yellow/20 text-zen-cal-yellow"}`}>
                      {b.status === "checked_in" ? "Checked-in" : b.status === "confirmed" ? "Confirmed" : "New"}
                    </span>
                  </div>
                  <div className="text-xs text-zen-muted mt-0.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {(b.start_time || "").slice(0, 5)} · {b.duration_minutes}m · {tech}
                  </div>
                  <div className="text-xs text-zen-ink/80 mt-1 line-clamp-2">{svcNames}</div>
                  {(b.customer_notes || "").trim() && (
                    <div className="text-xs text-amber-800 mt-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 leading-snug whitespace-pre-wrap"
                      data-testid={`mb-notes-${b.id}`}>
                      <Bell className="w-3 h-3 inline -mt-0.5 mr-1" strokeWidth={2.5} fill="currentColor" />
                      {b.customer_notes}
                    </div>
                  )}
                  {(b.customer_blacklisted || (b.customer_staff_note || "").trim()) && (
                    <div className="text-xs text-red-900 font-bold mt-1.5 bg-red-50 border-2 border-red-300 rounded-lg px-2 py-1.5 leading-snug whitespace-pre-wrap"
                      data-testid={`mb-sodden-body-${b.id}`}>
                      <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-red-700" strokeWidth={3} fill="currentColor" />
                      <span className="uppercase tracking-wide text-[10px] text-red-700 font-extrabold mr-1">
                        {b.customer_blacklisted ? "Blacklisted" : "Sổ đen"}:
                      </span>
                      {(b.customer_staff_note || "").trim() || "Do not accept — check with owner"}
                    </div>
                  )}
                  {b.customer_phone && (
                    <a href={`tel:${b.customer_phone}`} className="inline-flex items-center gap-1 text-xs text-zen-primary mt-1.5" data-testid={`mb-call-${b.id}`}>
                      <Phone className="w-3 h-3" /> {b.customer_phone}
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 mt-2.5">
                {isBooked && (
                  <button onClick={() => confirm(b)} className="flex-1 h-9 rounded-full bg-zen-primary text-white text-xs font-semibold flex items-center justify-center gap-1" data-testid={`mb-confirm-${b.id}`}>
                    <Check className="w-3.5 h-3.5" /> Confirm
                  </button>
                )}
                {(isBooked || isConfirmed) && (
                  <button onClick={() => checkIn(b)} className="flex-1 h-9 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid={`mb-checkin-${b.id}`}>
                    Check-in
                  </button>
                )}
                {(isBooked || isConfirmed) && (
                  <button onClick={() => markLate(b)} className="px-3 h-9 rounded-full bg-zen-surface text-xs font-semibold" data-testid={`mb-late-${b.id}`}>
                    Late
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Footer signed-in chip */}
      <div className="fixed bottom-3 left-1/2 -translate-x-1/2 bg-zen-ink/85 text-white text-[11px] px-3 py-1.5 rounded-full" data-testid="mb-whoami">
        {user?.full_name || user?.username}
      </div>
    </div>
  );
}

// Horizontal-scroll day grid for phones. 50px-wide staff columns + 50px time
// rail, 1px per minute (configurable). Renders every visible booking as an
// absolutely-positioned card so the grid doesn't get pushed around.
function CalendarGrid({ bookings, staff, svcById }) {
  const HOUR_PX = 60;          // 1 hour = 60px tall
  const COL_PX = 80;           // staff column width
  const RAIL_PX = 44;          // time rail width
  const DAY_START = 8 * 60;    // 8 AM
  const DAY_END = 21 * 60;     // 9 PM
  const totalMin = DAY_END - DAY_START;
  const pxPerMin = HOUR_PX / 60;
  const hours = [];
  for (let h = Math.ceil(DAY_START / 60); h <= Math.floor(DAY_END / 60); h++) hours.push(h);

  // group bookings by staff
  const byStaff = useMemo(() => {
    const map = {};
    bookings.filter((b) => !b.deleted && !b.is_block_time).forEach((b) => {
      (map[b.staff_id] ||= []).push(b);
    });
    return map;
  }, [bookings]);

  return (
    <div className="overflow-auto pb-24" data-testid="mb-cal-grid">
      <div className="relative inline-block" style={{ height: totalMin * pxPerMin + 30 }}>
        {/* Sticky header row with staff names */}
        <div className="sticky top-0 z-10 flex bg-white border-b border-zen-border">
          <div style={{ width: RAIL_PX }} className="flex-shrink-0" />
          {staff.map((s) => (
            <div key={s.id} style={{ width: COL_PX }}
              className="flex-shrink-0 px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-wide truncate border-l border-zen-border">
              {s.full_name}
            </div>
          ))}
        </div>
        {/* Body: rail + columns */}
        <div className="flex" style={{ height: totalMin * pxPerMin }}>
          <div style={{ width: RAIL_PX, height: totalMin * pxPerMin }} className="flex-shrink-0 relative">
            {hours.map((h) => {
              const top = (h * 60 - DAY_START) * pxPerMin;
              return (
                <div key={h} className="absolute left-0 right-0 text-[10px] text-zen-muted text-right pr-1"
                  style={{ top: top - 6 }}>
                  {h === 12 ? "12P" : h > 12 ? `${h - 12}P` : `${h}A`}
                </div>
              );
            })}
          </div>
          {staff.map((s) => (
            <div key={s.id} style={{ width: COL_PX, height: totalMin * pxPerMin }}
              className="flex-shrink-0 relative border-l border-zen-border">
              {/* Hour gridlines */}
              {hours.map((h) => (
                <div key={h} className="absolute left-0 right-0 border-t border-dashed border-zen-border/50"
                  style={{ top: (h * 60 - DAY_START) * pxPerMin }} />
              ))}
              {(byStaff[s.id] || []).map((b) => {
                const startMin = parseInt((b.start_time || "00:00").slice(0, 2)) * 60
                  + parseInt((b.start_time || "00:00").slice(3, 5));
                const top = (startMin - DAY_START) * pxPerMin;
                const height = Math.max(20, (b.duration_minutes || 30) * pxPerMin - 2);
                if (top < 0 || top > totalMin * pxPerMin) return null;
                const bg = b.status === "checked_in" ? "bg-emerald-200/90"
                  : b.status === "confirmed" ? "bg-blue-200/90"
                  : "bg-zen-cal-yellow/40";
                // Left stripe shows request (red) vs assign (green) — stays
                // visible even after check-in so reception can spot intent
                // at a glance.
                const stripeColor = b.requested_any ? "#0F4A2F" : "#7A1818";
                const svcNames = (b.service_ids || []).map((id) => svcById[id]?.name).filter(Boolean).join(", ");
                return (
                  <div key={b.id}
                    className={`absolute left-0.5 right-0.5 rounded ${bg} pl-1.5 pr-1 py-0.5 text-[9px] leading-tight overflow-hidden relative`}
                    style={{ top, height, boxShadow: `inset 3px 0 0 0 ${stripeColor}` }}
                    title={`${b.customer_name} — ${svcNames}`}
                    data-testid={`mb-cal-card-${b.id}`}>
                    <div className="font-bold truncate flex items-center gap-0.5">
                      {(b.customer_notes || "").trim() && (
                        <Bell className="w-2.5 h-2.5 text-amber-600 shrink-0" strokeWidth={2.5} fill="currentColor" />
                      )}
                      {(b.customer_blacklisted || (b.customer_staff_note || "").trim()) && (
                        <AlertTriangle className="w-2.5 h-2.5 text-red-700 shrink-0" strokeWidth={3} fill="currentColor" />
                      )}
                      {b.customer_review_requested && (
                        <Star className="w-2.5 h-2.5 text-yellow-600 shrink-0" strokeWidth={2.5} fill="currentColor" />
                      )}
                      <span className="truncate">{b.customer_name}</span>
                    </div>
                    <div className="truncate opacity-80">{(b.start_time || "").slice(0, 5)}</div>
                    <div className="truncate opacity-70">{svcNames}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
